import { getAttributeForMutation, mutationTypes } from './mutationUtils';
import { applyAdjectiveMutations, applyIncompleteSoftMutations, applySoftMutations, provokersSoftMutationsOnly } from './softMutations';
import { toLowerCase, wordGenders, wordNumbers, wordTopics, wordTypes } from './wordUtils';
import { applySpiranteMutations, provokersSpiranteMutationsOnly } from './spiranteMutations';
import { applyHardMutations, provokersHardMutationsOnly } from './hardMutations';
import { applyMixedMutations } from './mixedMutations';
import { applyIncompleteMutations, provokersIncompleteMutationsOnly } from './incompleteMutations';

const masculineNounsProvokingSoftMutation = [
  'aer', 'amzer', 'avel', 'c\'hwezh', 'c\'hoari', 'lur', 'revr', 're',
];

const getArticleTextForNoun = (nounText, articleText) => {
  if (!nounText) {
    return articleText;
  }

  const normalizedText = toLowerCase(nounText);
  const firstLetterLow = normalizedText[ 0 ];
  if (firstLetterLow === 'l') {
    return articleText.startsWith('a') ? 'al' : 'ul';

  } else if ('ndthaeiou'.indexOf(firstLetterLow) > -1) {
    return articleText.startsWith('a') ? 'an' : 'un';

  } else {
    return articleText.startsWith('a') ? 'ar' : 'ur';
  }
};


export function applyMutations (words) {
  words.forEach((word) => {
    applyMutation(word, words);
  });
}

export function applyMutation (word, words) {

  if (!words.length) { // empty sentence
    return;
  }

  const wordIdx = words.indexOf(word);

  word.wordTypeChoices = []; // reset

  const attributeForMutation = getAttributeForMutation(word);
  const rawText = word[ attributeForMutation ];
  const text = toLowerCase(rawText);

  let nextWord = null;
  let nextText = '';

  if (wordIdx <= words.length - 2) {
    nextWord = words[ wordIdx + 1 ];
    const nextAttributeForMutation = getAttributeForMutation(nextWord);
    const rawNextText = nextWord[ nextAttributeForMutation ];
    nextText = toLowerCase(rawNextText);
  }

  let previousWord = null;
  let previousText = '';

  if (wordIdx > 0) {
    previousWord = words[ wordIdx - 1 ];
    const previousAttributeForMutation = getAttributeForMutation(previousWord);
    const rawPreviousText = previousWord[ previousAttributeForMutation ];
    previousText = toLowerCase(rawPreviousText);
  }

  if (word.wordType === wordTypes.ARTICLE) {
    if (text) {
      word.resultContent = getArticleTextForNoun(nextText, text);
    }
  }

  if (provokersSoftMutationsOnly.includes(text)) {

    if (text === 'tra') {
      if (word.wordType === wordTypes.CONJUNCTION_SUBORDINATE) {
        applySoftMutations(wordIdx + 1, words);
      }
    } else {
      applySoftMutations(wordIdx + 1, words);
    }

  } else if ((text === 'en' && nextText === 'em')
    || (text === 'en' && nextText === 'eur')) { // provokersSoftMutationsOnly in two words
    if (wordIdx + 2 < words.length) {
      applySoftMutations(wordIdx + 2, words);
    }

  } else if (provokersSpiranteMutationsOnly.includes(text)) {
    applySpiranteMutations(wordIdx + 1, words);

  } else if (provokersHardMutationsOnly.includes(text)) {
    applyHardMutations(wordIdx + 1, words);

  } else if (provokersIncompleteMutationsOnly.includes(text)) {
    applyIncompleteMutations(wordIdx + 1, words);

  } else if (text === 'e') {
    if (word.wordType === wordTypes.PARTICLE_VERBAL) {
      applyMixedMutations(wordIdx + 1, words);

    } else if (word.wordType === wordTypes.ADJECTIVE_POSSESSIVE
      || word.wordType === wordTypes.PRONOUN_PERSONAL) {
      applySoftMutations(wordIdx + 1, words);
    } else {
      word.wordTypeChoices = [
        wordTypes.PRONOUN_PERSONAL,
        wordTypes.ADJECTIVE_POSSESSIVE,
        wordTypes.PARTICLE_VERBAL,
      ];
    }

  } else if (text === 'ma') {
    if (word.wordType === wordTypes.CONJUNCTION_SUBORDINATE) {
      applyMixedMutations(wordIdx + 1, words);

    } else if (word.wordType === wordTypes.ADJECTIVE_POSSESSIVE
      || word.wordType === wordTypes.PRONOUN) {
      applySpiranteMutations(wordIdx + 1, words);

    } else {
      word.wordTypeChoices = [
        wordTypes.CONJUNCTION_SUBORDINATE,
        wordTypes.ADJECTIVE_POSSESSIVE,
        wordTypes.PRONOUN,
      ];
    }

  } else if (text === 'o') {
    if (word.wordType === wordTypes.PARTICLE_VERBAL) {
      applyMixedMutations(wordIdx + 1, words);

    } else if (word.wordType === wordTypes.ADJECTIVE_POSSESSIVE
      || word.wordType === wordTypes.PRONOUN) {
      applySpiranteMutations(wordIdx + 1, words);

    } else {
      word.wordTypeChoices = [
        wordTypes.PRONOUN,
        wordTypes.ADJECTIVE_POSSESSIVE,
        wordTypes.PARTICLE_VERBAL,
      ];
    }

  } else if (text === 'em') {
    // check this isn't en em => spirante
    if (wordIdx > 0) {
      const previousWord = words[ wordIdx - 1 ];
      const previousAttributeForMutation = getAttributeForMutation(previousWord);
      const rawPreviousText = previousWord[ previousAttributeForMutation ];
      const previousText = toLowerCase(rawPreviousText);

      if (previousText !== 'en') { // not 'en em' (soft mutater)
        applySpiranteMutations(wordIdx + 1, words);
      }
    }
  } else if (text === 'ar' || text === 'an'
    || text === 'un' || text === 'ur'
    || text === 'en' || text === 'er'
  ) {

    if ((text === 'er' && word.wordType === wordTypes.PRONOUN)
      || (text === 'er' && (nextWord && nextWord.wordType === wordTypes.VERB))) {
      // kavout -> er c'havout (le trouver)
      applyIncompleteMutations(wordIdx + 1, words);

    } else if (nextWord && nextWord.wordType === wordTypes.NOUN) {

      if (nextWord.wordGender === wordGenders.MASCULINE) {

        if (nextWord.wordNumber === wordNumbers.SINGULAR) {

          if (nextText === 'tra') {

            nextWord.resultContent = 'dra';
            nextWord.sourceContent = 'tra';

          } else {
            applyIncompleteMutations(wordIdx + 1, words);
          }

        } else if (nextWord.wordNumber === wordNumbers.PLURAL) {

          if (nextWord.wordTopic === wordTopics.PEOPLE) {

            if (!nextText.endsWith('ou')) {
              applyIncompleteSoftMutations(wordIdx + 1, words);
            } // else no mutation (ex: an tadoù)

          } else {

            if (nextText === 'mein') {
              nextWord.resultContent = 'vein';
              nextWord.mutationCause = mutationTypes.INCOMPLETE;

            } else {
              applyIncompleteMutations(wordIdx + 1, words);
            }
          }

        } else {
          throw Error(`No number defined for the noun ${nextText}`);
        }

      } else if (nextWord.wordGender === wordGenders.FEMININE) {

        if (nextWord.wordNumber === wordNumbers.SINGULAR) {

          if (nextText === 'plac\'h'
            || nextText === 'gar'
            || nextText === 'greg') {
            // no mutation

          } else if (nextWord.sourceContent === 'dor') {
            nextWord.resultContent = 'nor';

          } else {
            applyIncompleteSoftMutations(wordIdx + 1, words);
          }

        } else if (nextWord.wordNumber === wordNumbers.PLURAL) {
          applyIncompleteMutations(wordIdx + 1, words);

        } else {
          throw Error(`No number defined for the noun ${nextText}`);
        }

      } else {
        throw Error(`No gender defined for the noun ${nextText}`);
      }

    } else if (nextWord && (nextWord.wordType === wordTypes.ADJECTIVE_QUALIFICATIVE
      || nextWord.wordType === wordTypes.CARDINAL_BEFORE_SUBSTANTIF
      || nextWord.wordType === wordTypes.COMPARATIVE
      || (nextWord.wordType === wordTypes.ORDINAL && nextWord.wordGender === wordGenders.MASCULINE)
    )) {
      applyIncompleteMutations(wordIdx + 1, words);

    } else {
      // nextWord.wordTypeChoices = [
      //   wordTypes.NOUN,
      //   wordTypes.ADJECTIVE,
      //   wordTypes.CARDINAL_BEFORE_SUBSTANTIF,
      //   wordTypes.COMPARATIVE,
      //   wordTypes.ORDINAL,
      // ];
    }


  } else if (text === 'hor') {
    applyIncompleteMutations(wordIdx + 1, words);

  } else if (word.wordType === wordTypes.ADJECTIVE_QUALIFICATIVE) {

    if (masculineNounsProvokingSoftMutation.includes(previousText)) {
      // aer (air) [2] : tomm → aer domm (air chaud)
      // amzer (temps) : brav → amzer vrav (beau temps)
      // avel (vent) : kreñv → avel greñv (vent fort)
      // c'hwezh (odeur) mat → c'hwezh vat (bonne odeur), mais kreñv → c'hwezh kreñv (forte odeur)
      // c'hoari (jeu) kaer → c'hoari gaer (beau jeu) [3]
      // lur (livre, unité de pids) : kig → ul lur gig (une livre de viande)
      // revr (cul) : berr → revr verr (cul court, qui a les jambes courtes) ; tev → daou revr dev (deux gros culs)
      // re (paire) : botoù → daou re votoù (deux paires de chaussures)
      applyAdjectiveMutations(wordIdx, words);

    } else if (previousWord && (
      (
        previousWord.wordGender === wordGenders.FEMININE && previousWord.wordNumber === wordNumbers.SINGULAR
      )
      ||
      (
        previousWord.wordGender === wordGenders.MASCULINE && previousWord.wordNumber === wordNumbers.PLURAL
        && previousWord.wordTopic === wordTopics.PEOPLE && !previousText.endsWith('ou')
      )
    )) {

      if (text.startsWith('k')
        || text.startsWith('p')
        || text.startsWith('t')
      ) {

        if (previousText.endsWith('l')
          || previousText.endsWith('n')
          || previousText.endsWith('m')
          || previousText.endsWith('r')
          || previousText.endsWith('v')
          || previousText.endsWith('a')
          || previousText.endsWith('e')
          || previousText.endsWith('i')
          || previousText.endsWith('o')
          || previousText.endsWith('u')
        ) {
          applyAdjectiveMutations(wordIdx, words);
        }
      } else if (text.startsWith('g')
        || text.startsWith('gw')
        || text.startsWith('b')
        || text.startsWith('d')
        || text.startsWith('m')
        || text.startsWith('c\'h')
        || text.startsWith('w')
        || text.startsWith('v')
      ) {
        applyAdjectiveMutations(wordIdx, words);
      }

    }
  }

  if (!word.resultContent) {
    word.resultContent = word.sourceContent;
  }

}
